/**
 * RFC-1023 (multi-canvas keep-alive), building on RFC-0088 (Yjs connection
 * lifecycle): warm YDoc-connection pool.
 *
 * Historically `useYDoc` created a fresh Y.Doc + WebsocketProvider on every
 * projectId/canvasId change and destroyed them on cleanup. That made switching
 * BETWEEN canvases of the same project tear down the connection and force a
 * backend cold-load + full CRDT resync every single time — including switching
 * *back* to a canvas the user just left — which re-showed the CanvasLoadOverlay
 * progress bar on each switch.
 *
 * This module keeps recently-viewed canvases' Y.Doc + provider ALIVE in a
 * module-scoped pool keyed by the full room name `${projectId}/${canvasId}`.
 * `useYDoc` becomes a thin subscriber: it `acquire()`s the entry for the active
 * canvas and `release()`s it on switch-away. Because the pool returns the SAME
 * Y.Doc object identity for a warm canvas (already synced in memory), switching
 * back needs no network round-trip and never re-shows the overlay.
 *
 * Auto-release (answers "what if memory is an issue?"):
 *   - LRU cap: `MAX_LIVE_CANVAS_ROOMS` bounds the number of live rooms. On
 *     acquire, non-active rooms beyond the cap are evicted oldest-first.
 *   - Idle eviction: a room with no active subscriber (`refCount === 0`) that
 *     stays idle longer than `IDLE_EVICT_MS` is evicted, freeing its WebSocket.
 *   - The active room (refCount > 0) is NEVER evicted.
 *
 * CRITICAL invariant preserved from the old hook: a Y.Doc is never reused
 * across projects. Entries are keyed by the full `${projectId}/${canvasId}`, and
 * on acquire any entry belonging to a *different* project is evicted, so the
 * Yjs CRDT sync protocol can never send a previous project's state into a new
 * project's room.
 */

import * as Y from 'yjs'
import { WebsocketProvider } from 'y-websocket'

import { getConfiguredApiBase, getAuthToken, getYDocRoomState } from '../api/client'
import { createDebug, setDebugClientID, createNadDebug } from '../utils/debugLog'

const log = createDebug('dw:ydoc:lifecycle')
// Feature-tagged logger for the initial-load failure / reconnect-termination
// path so `nad trace --feature ydoc-load-failure` can isolate it.
const loadFailureLog = createDebug('dw:ydoc-load-failure:reconnect')
// Feature-tagged logger for the keep-alive pool so `nad trace --feature
// canvas-keepalive` can isolate acquire / cold-load / warm-hit / evict.
const poolLog = createNadDebug('dw:canvas-keepalive:pool')

/** Access internal wsLastMessageReceived on WebsocketProvider (not in public types). */
function getWsLastMessageReceived(provider: WebsocketProvider): number | undefined {
  return provider.wsLastMessageReceived
}
function setWsLastMessageReceived(provider: WebsocketProvider, value: number): void {
  provider.wsLastMessageReceived = value
}

export interface YDocConnectionError {
  code: number
  reason: string
  message: string
}

export interface CanvasRoomSnapshot {
  /** The Yjs document for this room (stable identity while the entry is warm). */
  ydoc: Y.Doc
  /** Room name `${projectId}/${canvasId}`. */
  roomName: string
  /** Whether the initial sync with the server is complete. */
  synced: boolean
  /** Whether the WebSocket connection is live. */
  connected: boolean
  /** Terminal load error. When set, the provider has stopped reconnecting. */
  error: YDocConnectionError | null
  /**
   * The server's canvas generation for this room, or `null` until the room
   * state has been fetched.
   *
   * Exposed because undo/redo history must not survive a fork: the pool already
   * reloads on a change here, but `WorkspacePageV2` needs the value itself to
   * decide whether a PARKED canvas's history is still replayable (T289182224).
   * Deliberately not `ydoc.clientID` or the `Y.Doc` object identity — the pool
   * mints a fresh document on ordinary idle eviction, which is not a fork.
   */
  roomEpoch: number | null
}

// ---------------------------------------------------------------------------
// Tunables (see module docstring)
// ---------------------------------------------------------------------------

/** Max number of live canvas rooms (active + warm). The active room is exempt. */
export const MAX_LIVE_CANVAS_ROOMS = 4
/** Idle (refCount === 0) room lifetime before auto-release frees its WebSocket. */
export const IDLE_EVICT_MS = 5 * 60 * 1000

// ---------------------------------------------------------------------------
// Connection tunables — ported verbatim from the old useYDoc effect.
// ---------------------------------------------------------------------------

/** Liveness watchdog. y-websocket's resyncInterval=2s drives steady inbound
 *  traffic post-sync; the watchdog reads provider.wsLastMessageReceived every
 *  2s and force-closes when the gap exceeds the phase-appropriate threshold.
 *
 *  Two-phase timeout:
 *  - INITIAL_HEARTBEAT_DEAD_MS (120s) while waiting for the initial sync. Fat-
 *    project cold loads can legitimately take 15-20s, and on a freshly-booted
 *    instance under load can queue behind in-flight cold loads. Killing the WS
 *    during this phase produced the "wave of reconnects" storm.
 *  - HEARTBEAT_DEAD_MS (5s) AFTER the initial sync, when resyncInterval drives
 *    steady traffic so a 5s gap is a real signal of a dead connection. */
const HEARTBEAT_INTERVAL_MS = 2000
const HEARTBEAT_DEAD_MS = 5000
const INITIAL_HEARTBEAT_DEAD_MS = 120000
const ROOM_RESET_CLOSE_CODE = 4101
// The canvas was deleted server-side. Distinct from a room RESET on purpose:
// a reset reloads the page, and the backend evicts the room BEFORE the delete
// is durable and before the HTTP response, so reloading would land the user
// back on a canvas that is still indexed and about to disappear. Tear the room
// down quietly instead and let the delete flow navigate.
const CANVAS_DELETED_CLOSE_CODE = 4104
const CANVAS_DELETED_CLOSE_REASON = 'canvas_deleted'
const ROOM_RESET_CLOSE_REASON = 'ydoc_room_epoch_reset'
const YDOC_LOAD_FAILED_CLOSE_CODE = 4102
const YDOC_LOAD_FAILED_CLOSE_REASON = 'ydoc_load_failed'
// RFC-1045 / T281165369: a RECOVERABLE transient Cloud SQL connectivity blip
// during room-control read. Unlike the terminal 4102, this is retryable — route
// it to the bounded initial-load budget so the client self-heals instead of
// showing the "project data needs repair" corruption copy.
const YDOC_LOAD_TRANSIENT_CLOSE_CODE = 4103
const YDOC_LOAD_TRANSIENT_CLOSE_REASON = 'ydoc_load_transient_infra'

/** Synthetic terminal error for an initial load that never connected/synced. */
const CONNECT_FAILED_CODE = 4000
const CONNECT_FAILED_REASON = 'ydoc_connect_failed'

/**
 * Safety net against an infinite "loading YDoc" spinner. Caps the number of
 * *initial-load* failures (closes/errors/connect throws before the first sync);
 * once exceeded we surface a terminal error and stop reconnecting. Resets on the
 * first successful sync so a healthy session's later transient blips keep
 * retrying indefinitely.
 */
const MAX_INITIAL_LOAD_ATTEMPTS = 8

/**
 * Convert an HTTP(S) API base URL to a WebSocket URL.
 * e.g. "http://localhost:9000" → "ws://localhost:9000"
 */
function toWsUrl(httpUrl: string): string {
  return httpUrl.replace(/^http/, 'ws')
}

// ---------------------------------------------------------------------------
// Pool internals
// ---------------------------------------------------------------------------

interface PoolEntry {
  projectId: string
  canvasId: string
  roomName: string
  ydoc: Y.Doc
  provider: WebsocketProvider | null
  synced: boolean
  connected: boolean
  error: YDocConnectionError | null
  /** Number of live React subscribers (0 while warm-but-idle). */
  refCount: number
  /** Epoch-ms of the last acquire/release; drives LRU + idle eviction. */
  lastUsedTs: number
  subscribers: Set<() => void>
  /** True once evicted — all handlers no-op afterwards. */
  destroyed: boolean
  // internal lifecycle handles
  watchdogTimer: ReturnType<typeof setInterval> | null
  connectRetryTimer: ReturnType<typeof setTimeout> | null
  idleEvictTimer: ReturnType<typeof setTimeout> | null
  onVisibilityChange: (() => void) | null
  everSynced: boolean
  failedLoadAttempts: number
  /** Server canvas generation, `null` until the first room-state fetch. */
  roomEpoch: number | null
}

const pool = new Map<string, PoolEntry>()
/** The most-recently-acquired room; used to scope DEV window globals. */
let activeRoomName: string | null = null

export type WebSocketDrainResult = 'drained' | 'timed-out' | 'no-active-room' | 'socket-not-open'

/** Wait until the browser has handed all queued websocket bytes to the network. */
export function waitForWebSocketBufferToDrain(
  socket: Pick<WebSocket, 'bufferedAmount'>,
  timeoutMs: number,
): Promise<'drained' | 'timed-out'> {
  if (socket.bufferedAmount === 0) return Promise.resolve('drained')

  const startedAt = Date.now()
  return new Promise((resolve) => {
    const poll = () => {
      if (socket.bufferedAmount === 0) {
        resolve('drained')
        return
      }
      if (Date.now() - startedAt >= timeoutMs) {
        resolve('timed-out')
        return
      }
      window.setTimeout(poll, 10)
    }
    window.setTimeout(poll, 10)
  })
}

/**
 * Best-effort wait for the browser to hand queued Yjs bytes to the network.
 * This is not a server delivery or durability acknowledgement.
 */
export async function waitForActiveRoomOutboundSend(timeoutMs: number): Promise<WebSocketDrainResult> {
  if (!activeRoomName) return 'no-active-room'
  const provider = pool.get(activeRoomName)?.provider
  const socket = provider?.ws
  if (!socket || socket.readyState !== WebSocket.OPEN) return 'socket-not-open'
  return waitForWebSocketBufferToDrain(socket, timeoutMs)
}

function roomNameOf(projectId: string, canvasId: string): string {
  return `${projectId}/${canvasId}`
}

/** Notify all React subscribers of an entry that its snapshot changed. */
function notify(entry: PoolEntry): void {
  entry.subscribers.forEach((fn) => {
    try {
      fn()
    } catch {
      /* a subscriber throwing must not break others / the provider */
    }
  })
}

/** Expose Y.Doc / provider on window for Playwright + E2E (dev only, active room only). */
function maybeExposeDevGlobals(entry: PoolEntry): void {
  if (!import.meta.env.DEV) return
  if (entry.roomName !== activeRoomName) return
  window.__ydoc = entry.ydoc
  window.__Y = Y
  if (entry.provider) window.__ydocProvider = entry.provider
}

/**
 * Tear down an entry's provider/doc/timers and remove it from the pool.
 * Mirrors the old useYDoc cleanup exactly.
 */
function evict(
  roomName: string,
  reason: 'lru' | 'project-switch' | 'idle' | 'unmount' | 'room-reset' | 'stale' | 'canvas-deleted',
): void {
  const entry = pool.get(roomName)
  if (!entry) return
  poolLog({ event: 'evict', roomName, reason, refCount: entry.refCount, lastUsedTs: entry.lastUsedTs })
  log('keepalive evict room=%s reason=%s (clientID=%d)', roomName, reason, entry.ydoc?.clientID ?? -1)

  entry.destroyed = true
  if (entry.watchdogTimer != null) {
    clearInterval(entry.watchdogTimer)
    entry.watchdogTimer = null
  }
  if (entry.connectRetryTimer != null) {
    clearTimeout(entry.connectRetryTimer)
    entry.connectRetryTimer = null
  }
  if (entry.idleEvictTimer != null) {
    clearTimeout(entry.idleEvictTimer)
    entry.idleEvictTimer = null
  }
  if (entry.onVisibilityChange) {
    document.removeEventListener('visibilitychange', entry.onVisibilityChange)
    entry.onVisibilityChange = null
  }
  if (import.meta.env.DEV && entry.roomName === activeRoomName) {
    delete window.__ydocProvider
  }
  if (entry.provider) {
    entry.provider.destroy()
    entry.provider = null
  }
  entry.ydoc.destroy()
  pool.delete(roomName)
}

/**
 * Evict the least-recently-used, non-active (refCount === 0) entries until the
 * pool is within MAX_LIVE_CANVAS_ROOMS. The active room is never evicted.
 */
function enforceLruCap(): void {
  if (pool.size <= MAX_LIVE_CANVAS_ROOMS) return
  const evictable = [...pool.values()]
    .filter((e) => e.refCount === 0)
    .sort((a, b) => a.lastUsedTs - b.lastUsedTs)
  for (const entry of evictable) {
    if (pool.size <= MAX_LIVE_CANVAS_ROOMS) break
    evict(entry.roomName, 'lru')
  }
}

/** (Re)arm the idle-eviction timer for a warm-but-idle entry. */
function armIdleEvict(entry: PoolEntry): void {
  if (entry.idleEvictTimer != null) {
    clearTimeout(entry.idleEvictTimer)
    entry.idleEvictTimer = null
  }
  entry.idleEvictTimer = setTimeout(() => {
    const current = pool.get(entry.roomName)
    // Only evict if still the same entry AND still idle.
    if (current === entry && current.refCount === 0) {
      evict(entry.roomName, 'idle')
    }
  }, IDLE_EVICT_MS)
}

/**
 * Start the WebSocket connection lifecycle for a freshly-created entry.
 * This is the machinery ported verbatim from the old useYDoc effect
 * (connect / watchdog / room-epoch fence / terminal-error budget / retry),
 * with the closure `cancelled` flag replaced by `entry.destroyed`.
 */
function startConnection(entry: PoolEntry): void {
  const { projectId, canvasId, roomName, ydoc: freshDoc } = entry

  const apiBase = getConfiguredApiBase()
  const wsBase = toWsUrl(apiBase)
  const wsUrl = `${wsBase}/ws/yjs`

  const stopWatchdog = () => {
    if (entry.watchdogTimer != null) {
      clearInterval(entry.watchdogTimer)
      entry.watchdogTimer = null
    }
  }
  const clearConnectRetry = () => {
    if (entry.connectRetryTimer != null) {
      clearTimeout(entry.connectRetryTimer)
      entry.connectRetryTimer = null
    }
  }

  // When tab returns to foreground, reset wsLastMessageReceived so a long
  // background-throttled gap doesn't immediately trigger a false-positive
  // reconnect. The next 2s sync cycle will surface a real disconnect within
  // HEARTBEAT_DEAD_MS.
  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible' && entry.provider) {
      setWsLastMessageReceived(entry.provider, Date.now())
    }
  }
  entry.onVisibilityChange = onVisibilityChange
  document.addEventListener('visibilitychange', onVisibilityChange)

  const reloadForRoomReset = (reason: string) => {
    if (entry.destroyed) return
    // A background (non-active, refCount === 0) warm room must NOT reload the
    // whole workspace on a 4101/epoch reset — the user is on another canvas.
    // Evict it instead; a later re-acquire cold-loads fresh against the new
    // room epoch. Only the active room (which the user is looking at) reloads.
    if (entry.refCount === 0) {
      log('room reset on background room=%s reason=%s — evicting instead of reloading', roomName, reason)
      evict(roomName, 'room-reset')
      return
    }
    entry.destroyed = true
    log('room reset required room=%s reason=%s', roomName, reason)
    poolLog({ event: 'room-reset-reload', roomName, reason, refCount: entry.refCount })
    window.location.reload()
  }

  const setSynced = (value: boolean) => {
    if (entry.synced === value) return
    entry.synced = value
    notify(entry)
  }
  const setConnected = (value: boolean) => {
    if (entry.connected === value) return
    entry.connected = value
    notify(entry)
  }

  const failWithTerminalError = (nextError: YDocConnectionError) => {
    if (entry.destroyed) return
    log('terminal ydoc load error room=%s code=%d reason=%s', roomName, nextError.code, nextError.reason)
    poolLog({ event: 'terminal-error', roomName, code: nextError.code, reason: nextError.reason })
    stopWatchdog()
    clearConnectRetry()
    entry.connected = false
    entry.synced = false
    entry.error = nextError
    notify(entry)
    entry.destroyed = true
    if (entry.provider) {
      entry.provider.destroy()
      entry.provider = null
    }
    // NOTE: the Y.Doc is intentionally NOT destroyed here — subscribers still
    // reference it to render the error state. It is torn down when the entry is
    // evicted or replaced on re-acquire (see acquireRoom).
  }

  // Count a failed initial-load attempt (WS close/error or connect() throw
  // before the first sync). Once the budget is exhausted, surface a terminal
  // error so the loading overlay reports the failure instead of spinning
  // forever. No-op once synced.
  const registerFailedLoadAttempt = (
    context: string,
    // Terminal error to surface if this attempt exhausts the budget. Defaults to
    // the generic connect-failed copy; a transient-infra close (4103) passes an
    // ACCURATE "temporary connection issue" error instead of the "needs repair"
    // corruption copy.
    terminalErrorOnExhaustion?: YDocConnectionError,
  ) => {
    if (entry.destroyed || entry.everSynced) return
    entry.failedLoadAttempts += 1
    loadFailureLog(
      'initial-load attempt failed room=%s context=%s attempt=%d/%d',
      roomName,
      context,
      entry.failedLoadAttempts,
      MAX_INITIAL_LOAD_ATTEMPTS,
    )
    if (entry.failedLoadAttempts >= MAX_INITIAL_LOAD_ATTEMPTS) {
      loadFailureLog(
        'initial-load budget exhausted room=%s — failing loud and stopping reconnect',
        roomName,
      )
      failWithTerminalError(
        terminalErrorOnExhaustion ?? {
          code: CONNECT_FAILED_CODE,
          reason: CONNECT_FAILED_REASON,
          message:
            'Could not connect to the canvas room after several attempts. ' +
            'Check your connection and reload; if this keeps happening, the ' +
            'project data may need repair.',
        },
      )
    }
  }

  const connect = async () => {
    const roomState = await getYDocRoomState(projectId, canvasId)
    const token = await getAuthToken()
    if (entry.destroyed) return

    log(
      'created fresh Y.Doc for %s/%s (clientID=%d room_epoch=%d)',
      projectId,
      canvasId,
      freshDoc.clientID,
      roomState.room_epoch,
    )

    // Publish the generation before the provider opens, so a consumer that
    // renders on `synced` never sees a synced room with an unknown epoch.
    entry.roomEpoch = roomState.room_epoch
    notify(entry)

    const params: Record<string, string> = {}
    if (token) {
      params['token'] = token
    }
    params['room_epoch'] = String(roomState.room_epoch)

    const provider = new WebsocketProvider(wsUrl, roomName, freshDoc, {
      connect: true,
      params,
      // Browser BroadcastChannel sync can replay a stale tab's Y.Doc into a
      // freshly reset room before the websocket epoch fence runs. Keep tabs
      // synchronized through the backend room only.
      disableBc: true,
      // Sync messages serve as both real state-sync and as inbound traffic
      // that the watchdog uses for liveness. 2s gives the watchdog (5s
      // timeout) enough samples to detect silent disconnects within ~5s.
      resyncInterval: 2000,
      // Cap above the y-websocket default of 2.5s so a transient blip doesn't
      // hammer the server with too many reconnect attempts.
      maxBackoffTime: 5000,
    })
    entry.provider = provider

    // Race guard: the entry can be evicted (e.g. React StrictMode's
    // mount→unmount→remount collapsing to a release, or a fast project switch)
    // while connect() is suspended on the room-state/token awaits above. If
    // eviction set `destroyed` after those awaits passed, this provider would
    // otherwise be orphaned. Destroy it immediately.
    if (entry.destroyed) {
      provider.destroy()
      entry.provider = null
      return
    }

    maybeExposeDevGlobals(entry)

    provider.on('status', async ({ status }: { status: string }) => {
      if (entry.destroyed) return
      log('ws status=%s room=%s', status, roomName)
      setConnected(status === 'connected')
      if (status === 'disconnected') {
        try {
          const latestRoomState = await getYDocRoomState(projectId, canvasId)
          if (latestRoomState.room_epoch !== roomState.room_epoch) {
            reloadForRoomReset(
              `room_epoch_changed:${roomState.room_epoch}->${latestRoomState.room_epoch}`,
            )
            return
          }
          const freshToken = await getAuthToken()
          if (freshToken && provider?.params) {
            provider.params['token'] = freshToken
            provider.params['room_epoch'] = String(roomState.room_epoch)
          }
          if (!freshToken) {
            log('auth token null — potential session expiry')
          }
        } catch (err) {
          log('token refresh failed: %O', err)
        }
      }
    })

    // Tracks whether the y-websocket sync handshake has completed on the
    // current connection. Used by the watchdog to pick between the longer
    // INITIAL_HEARTBEAT_DEAD_MS (pre-sync) and the shorter HEARTBEAT_DEAD_MS
    // (post-sync). y-websocket emits sync=false on disconnect, so this resets
    // across reconnects automatically.
    const syncedRef = { current: false }
    provider.on('sync', (isSynced: boolean) => {
      if (entry.destroyed) return
      syncedRef.current = isSynced
      if (isSynced) {
        const wasSynced = entry.everSynced
        // A successful sync clears the initial-load failure budget so later
        // transient disconnects on a live session keep retrying.
        entry.everSynced = true
        entry.failedLoadAttempts = 0
        // A warm entry whose socket dropped and re-synced in the background.
        if (wasSynced) {
          poolLog({ event: 'reconnect', roomName })
        }
      }
      log('ws synced=%s room=%s', isSynced, roomName)
      setSynced(isSynced)
    })

    provider.on('connection-close', (event: CloseEvent | null) => {
      const code = event?.code
      const reason = event?.reason || ''
      if (code === CANVAS_DELETED_CLOSE_CODE || reason.includes(CANVAS_DELETED_CLOSE_REASON)) {
        log('canvas deleted server-side room=%s — evicting without reload', roomName)
        poolLog({ event: 'canvas-deleted', roomName })
        evict(roomName, 'canvas-deleted')
        return
      }
      if (code === ROOM_RESET_CLOSE_CODE || reason.includes(ROOM_RESET_CLOSE_REASON)) {
        reloadForRoomReset(`ws_close:${code ?? 'unknown'}:${reason}`)
        return
      }
      if (code === YDOC_LOAD_FAILED_CLOSE_CODE || reason.includes(YDOC_LOAD_FAILED_CLOSE_REASON)) {
        failWithTerminalError({
          code: code ?? YDOC_LOAD_FAILED_CLOSE_CODE,
          reason: reason || YDOC_LOAD_FAILED_CLOSE_REASON,
          // CanvasLoadOverlay renders localized copy keyed off error.reason
          // (ydoc_load_failed → canvasArea.ydocError.loadFailed); this English
          // string is a fallback shown only for unrecognized reasons.
          // i18n-exempt: fallback only, localized by reason in CanvasLoadOverlay
          message: 'The server could not load this canvas room. The project data needs repair before it can open.',
        })
        return
      }
      // RFC-1045 / T281165369: a transient Cloud SQL connectivity blip during
      // room-control read is RECOVERABLE — route it to the bounded initial-load
      // budget so the client auto-reconnects and self-heals. Only after the
      // budget is exhausted do we surface a terminal error, with an accurate
      // "temporary connection issue" message (NOT the "needs repair" copy).
      if (
        code === YDOC_LOAD_TRANSIENT_CLOSE_CODE ||
        reason.includes(YDOC_LOAD_TRANSIENT_CLOSE_REASON)
      ) {
        registerFailedLoadAttempt(`transient-infra:${code ?? 'unknown'}`, {
          code: YDOC_LOAD_TRANSIENT_CLOSE_CODE,
          reason: YDOC_LOAD_TRANSIENT_CLOSE_REASON,
          // CanvasLoadOverlay renders localized copy keyed off error.reason
          // (ydoc_load_transient_infra → canvasArea.ydocError.transientInfra);
          // this English string is a fallback shown only for unrecognized reasons.
          // i18n-exempt: fallback only, localized by reason in CanvasLoadOverlay
          message:
            'A temporary connection issue is preventing this canvas from loading. ' +
            'Please check your connection and reload — this usually resolves on its own.',
        })
        return
      }
      // Any other close before the first sync counts toward the initial-load
      // budget so a server that never sends a terminal code can't keep us in an
      // infinite reconnect loop.
      registerFailedLoadAttempt(`connection-close:${code ?? 'unknown'}`)
    })

    // NOTE: do NOT count here. y-websocket can emit BOTH 'connection-error' and
    // 'connection-close' for a single socket failure; counting both would trip
    // the cap in half the intended attempts. 'connection-close' is the
    // canonical per-attempt signal. This handler is log-only.
    provider.on('connection-error', (event: Event | null) => {
      if (entry.destroyed) return
      loadFailureLog('ws connection-error room=%s type=%s', roomName, event?.type ?? 'unknown')
    })

    setDebugClientID(freshDoc.clientID)

    // Liveness watchdog: y-websocket's onmessage updates wsLastMessageReceived.
    // If no inbound message for the phase-appropriate threshold, force-close the
    // underlying ws — y-websocket's onclose schedules an automatic reconnect.
    stopWatchdog()
    entry.watchdogTimer = setInterval(() => {
      if (entry.destroyed || !entry.provider) return
      const lastMs = getWsLastMessageReceived(entry.provider)
      if (!lastMs) return
      const deadMs = syncedRef.current ? HEARTBEAT_DEAD_MS : INITIAL_HEARTBEAT_DEAD_MS
      if (Date.now() - lastMs > deadMs) {
        log(
          'ws watchdog-timeout room=%s synced=%s threshold=%dms — forcing reconnect',
          roomName,
          syncedRef.current,
          deadMs,
        )
        if (entry.provider.ws) {
          try { entry.provider.ws.close() } catch { /* already closed */ }
        }
      }
    }, HEARTBEAT_INTERVAL_MS)
  }

  // connect() can throw before a provider exists (room-state preflight / auth
  // token fetch). y-websocket only auto-retries AFTER a provider is created, so
  // a pre-provider throw would otherwise leave a silent spinner with no retry.
  // Retry with bounded backoff, sharing the initial-load budget.
  const attemptConnect = () => {
    void connect().catch((err) => {
      if (entry.destroyed) return
      log('ws setup failed room=%s: %O', roomName, err)
      entry.connected = false
      entry.synced = false
      notify(entry)
      registerFailedLoadAttempt('connect-throw')
      if (entry.destroyed) return
      const delay = Math.min(5000, 500 * 2 ** entry.failedLoadAttempts)
      clearConnectRetry()
      entry.connectRetryTimer = setTimeout(() => {
        if (!entry.destroyed) attemptConnect()
      }, delay)
    })
  }
  attemptConnect()
}

// ---------------------------------------------------------------------------
// Public pool API (consumed by useYDoc)
// ---------------------------------------------------------------------------

/**
 * Acquire the pool entry for a room, creating + connecting it on a cache miss
 * (cold load) or returning the warm entry on a cache hit. Increments the
 * entry's refCount; the caller MUST pair this with `releaseRoom(roomName)`.
 */
export function acquireRoom(projectId: string, canvasId: string): PoolEntry {
  const roomName = roomNameOf(projectId, canvasId)
  activeRoomName = roomName

  // Cross-project safety: never keep another project's rooms warm. Evict them
  // regardless of refCount (only one workspace is mounted at a time, so their
  // subscribers are already gone).
  for (const entry of [...pool.values()]) {
    if (entry.projectId !== projectId) {
      evict(entry.roomName, 'project-switch')
    }
  }

  let entry = pool.get(roomName)
  const warmHit = entry != null && !entry.destroyed
  if (entry?.destroyed) {
    // A terminal-errored / stale entry lingers in the pool (its subscribers were
    // rendering the error state). Fully tear it down — including its Y.Doc —
    // before creating the fresh replacement so nothing leaks.
    evict(roomName, 'stale')
    entry = undefined
  }
  if (!entry) {
    entry = {
      projectId,
      canvasId,
      roomName,
      ydoc: new Y.Doc(),
      provider: null,
      synced: false,
      connected: false,
      error: null,
      refCount: 0,
      lastUsedTs: Date.now(),
      subscribers: new Set(),
      destroyed: false,
      watchdogTimer: null,
      connectRetryTimer: null,
      idleEvictTimer: null,
      onVisibilityChange: null,
      everSynced: false,
      failedLoadAttempts: 0,
      // Unknown until `connect()` fetches the room state. Consumers treat null
      // as "cannot decide", never as "generation 0".
      roomEpoch: null,
    }
    pool.set(roomName, entry)
    poolLog({ event: 'cold-load', roomName })
    startConnection(entry)
  } else {
    // Cache hit: reuse the warm, already-connected Y.Doc. This is the event
    // that proves the fix — switching back needs no cold-load / resync.
    poolLog({ event: 'warm-hit', roomName, synced: entry.synced })
  }

  entry.refCount += 1
  entry.lastUsedTs = Date.now()
  // No longer idle — cancel any pending idle eviction.
  if (entry.idleEvictTimer != null) {
    clearTimeout(entry.idleEvictTimer)
    entry.idleEvictTimer = null
  }
  maybeExposeDevGlobals(entry)

  poolLog({
    event: 'acquire',
    roomName,
    warmHit,
    poolSize: pool.size,
    refCount: entry.refCount,
  })

  enforceLruCap()
  return entry
}

/**
 * Release a previously-acquired room. Decrements refCount; the entry stays
 * WARM in the pool (that is what makes switch-back instant). It is only torn
 * down later by LRU eviction or the idle-eviction timer.
 */
export function releaseRoom(roomName: string): void {
  const entry = pool.get(roomName)
  if (!entry) return
  entry.refCount = Math.max(0, entry.refCount - 1)
  entry.lastUsedTs = Date.now()
  poolLog({ event: 'release', roomName, refCount: entry.refCount })
  if (entry.refCount === 0) {
    armIdleEvict(entry)
  }
}

/**
 * Evict a project's IDLE (refCount === 0) rooms immediately.
 *
 * Called when the workspace unmounts (or the project changes) so leaving the
 * workspace — e.g. navigating to the dashboard — doesn't leave warm background
 * canvases holding authenticated WebSockets for the full IDLE_EVICT_MS window.
 * refCount-gated so it can never tear down a live active room (also makes it
 * safe against React StrictMode's spurious mount→unmount→remount).
 */
export function evictIdleProjectRooms(projectId: string): void {
  for (const entry of [...pool.values()]) {
    if (entry.projectId === projectId && entry.refCount === 0) {
      evict(entry.roomName, 'unmount')
    }
  }
}

/** Read the current snapshot for an entry (stable ydoc identity while warm). */
export function readSnapshot(entry: PoolEntry): CanvasRoomSnapshot {
  return {
    ydoc: entry.ydoc,
    roomName: entry.roomName,
    synced: entry.synced,
    connected: entry.connected,
    error: entry.error,
    roomEpoch: entry.roomEpoch,
  }
}

/** Subscribe a React consumer to an entry's snapshot changes. Returns unsubscribe. */
export function subscribeRoom(entry: PoolEntry, fn: () => void): () => void {
  entry.subscribers.add(fn)
  return () => {
    entry.subscribers.delete(fn)
  }
}

/** Test/diagnostic helper: current pool size. */
export function _poolSizeForTest(): number {
  return pool.size
}

/**
 * Test-only: synchronously evict every entry and reset module state.
 *
 * The pool is module-scoped, so it survives across test cases within a file
 * (vitest `isolate:true` gives a fresh module registry per FILE, not per case).
 * Suites that mount `useYDoc` for the same room across multiple cases must call
 * this in `beforeEach`, otherwise the second case gets a warm-hit and never
 * constructs a fresh `WebsocketProvider`.
 */
export function __resetCanvasRoomPoolForTest(): void {
  for (const roomName of [...pool.keys()]) {
    evict(roomName, 'unmount')
  }
  activeRoomName = null
}
